import * as React from "react";
import { OutlookHeader, SectionLabel } from "./components";
import { Icon, TYPE_VISUAL } from "./icons";
import { entityWebUrl } from "../auth/config";
import type { CaseEntity, Connection } from "./types";

/* global Office, window */

/** Open a URL in the user's default browser via Office's cross-host API. */
function openExternal(url: string): void {
  if (typeof Office !== "undefined" && Office.context?.ui?.openBrowserWindow) {
    Office.context.ui.openBrowserWindow(url);
    return;
  }
  window.open(url, "_blank", "noopener");
}

export function ConnectionDetail({
  connection,
  onBack,
}: {
  connection: Connection;
  onBack: () => void;
}): JSX.Element {
  const e = connection.entity;
  const epicUrl = entityWebUrl(e.kind, e.id, e.typename);

  return (
    <>
      <OutlookHeader
        leadingKind="back"
        title={e.typeLabel ?? TYPE_VISUAL[e.kind].label}
        // subtitle={e.title} // CLAUDE: I don't want subtitle.
        onLeading={onBack}
      />
      <div className="pane">
        <div className="detail-hero">
          <span className="detail-hero-emoji" aria-hidden="true">
            {e.emoji ?? TYPE_VISUAL[e.kind].emoji}
          </span>
          <h2>{e.title}</h2>
          <div className="meta-line">
            {e.kind === "case" && e.ref && <span className="ref">{e.ref}</span>}
            {/* Case meta is the "Due ..." string — same data the Case
                details section below shows in full. Skip it on the hero
                to avoid the duplication. Other kinds keep meta for the
                row context they bring (e.g. Task due, Car make/model). */}
            {e.kind !== "case" && e.meta && <span>{e.meta}</span>}
          </div>
        </div>

        {epicUrl && (
          <button
            type="button"
            className="add-connection-btn center-text"
            onClick={() => openExternal(epicUrl)}
            title="Open in EPIC (new window)"
            aria-label={`Open ${e.title} in EPIC (new window)`}
          >
            <span className="icn"><Icon.External /></span>
            <span className="text">Open in EPIC</span>
          </button>
        )}

        {e.kind === "case" ? (
          <CaseBody caseEntity={e} />
        ) : (
          <DefaultBody connection={connection} />
        )}
      </div>
    </>
  );
}

// ── Case-specific body ──────────────────────────────────────────────

function CaseBody({ caseEntity }: { caseEntity: CaseEntity }): JSX.Element {
  const start = pickDate(caseEntity.actualStart, caseEntity.scheduledStart);
  const end = pickDate(caseEntity.actualEnd, caseEntity.scheduledEnd);

  return (
    <>
      <SectionLabel title="Case details" />
      <div className="kv-list">
        <div className="kv">
          <span className="k">Status</span>
          <span className="v">{titleCase(caseEntity.status)}</span>
        </div>
        <div className="kv">
          <span className="k">Priority</span>
          <span className="v">{titleCase(caseEntity.priority)}</span>
        </div>
        {start && (
          <div className="kv">
            <span className="k">Start</span>
            <span className="v">
              <span className="kv-prefix">{start.kind}</span> {fmtDate(start.iso)}
            </span>
          </div>
        )}
        {end && (
          <div className="kv">
            <span className="k">End</span>
            <span className="v">
              <span className="kv-prefix">{end.kind}</span> {fmtDate(end.iso)}
            </span>
          </div>
        )}
      </div>
    </>
  );
}

// ── Default body (non-case kinds) ───────────────────────────────────

function DefaultBody({ connection }: { connection: Connection }): JSX.Element {
  const e = connection.entity;
  return (
    <>
      <SectionLabel title="Connection details" />
      <div className="kv-list">
        <div className="kv">
          <span className="k">Type</span>
          <span className="v">{e.typeLabel ?? TYPE_VISUAL[e.kind].label}</span>
        </div>
        <div className="kv">
          <span className="k">Linked</span>
          <span className="v">{fmtDate(connection.linkedAt)}</span>
        </div>
        {connection.role && (
          <div className="kv">
            <span className="k">Role</span>
            <span className="v">{connection.role}</span>
          </div>
        )}
        {connection.remarks && (
          <div className="kv kv-block">
            <span className="k">Remarks</span>
            <span className="v v-block">{connection.remarks}</span>
          </div>
        )}
      </div>
    </>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function pickDate(
  actual: string | undefined,
  scheduled: string | undefined
): { iso: string; kind: "actual" | "scheduled" } | null {
  if (actual) return { iso: actual, kind: "actual" };
  if (scheduled) return { iso: scheduled, kind: "scheduled" };
  return null;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function titleCase(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
